import { useItem } from '@/hooks/useItems';
import { useFavoriteAction } from '@/hooks/useFavorites';
import { ROLE_SLUG } from '@/constants/role-policy';
import { Button } from '@/components/ui/Button';
import { DesktopDrawer } from '@/components/ui/DesktopDrawer';
import { useToast } from '@/components/ui/Toast';
import type { ItemFilterSearch } from '@/lib/validation';
import { buildSiteHref } from '@/lib/site-paths';
import { useAuthStore } from '@/stores/auth-store';
import { ItemDetailView } from './ItemDetailView';
import { ItemThumbnailDropButton } from './ItemThumbnailDropButton';

interface ItemDetailDrawerProps {
  open: boolean;
  itemId: number | null;
  onClose: () => void;
  backToSearch?: ItemFilterSearch;
}

export function ItemDetailDrawer({ open, itemId, onClose, backToSearch }: ItemDetailDrawerProps) {
  const { data: item } = useItem(itemId);
  const favorite = useFavoriteAction(itemId ?? 0);
  const { toast } = useToast();
  const user = useAuthStore((state) => state.user);
  const isAdminUser = Boolean(user?.isSuperAdmin || user?.roles.includes(ROLE_SLUG.ADMINISTRATOR));
  const canEditItem = isAdminUser && Boolean(item?.adminEditUrl);
  const editableItemId = canEditItem && item ? item.id : null;
  const code = item?.code?.trim() ?? '';
  const hasPreviewHeaderTarget = Boolean(code)
    && Boolean(item?.previewViewports.desktop || item?.previewViewports.tablet || item?.previewViewports.mobile);

  return (
    <DesktopDrawer open={open} ariaLabel={item?.title ?? 'Item details'} onRequestClose={onClose}>
      <div className="space-y-5" data-ai-id="item-detail-drawer">
        {!hasPreviewHeaderTarget ? (
          <div className="flex items-center justify-end gap-3" data-ai-id="item-detail-drawer-header">
            <div className="flex flex-nowrap items-center gap-2 overflow-x-auto whitespace-nowrap" data-ai-id="item-detail-drawer-actions">
              {canEditItem ? (
                <>
                  {editableItemId ? <ItemThumbnailDropButton itemId={editableItemId} onSuccess={onClose} /> : null}
                  <a
                    href={item?.adminEditUrl ?? undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-8 shrink-0 items-center justify-center gap-2 border border-[#ccc] bg-[#f5f5f5] px-3 text-xs font-bold text-[#333] transition-none hover:bg-[#e6e6e6] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    data-ai-id="item-detail-edit-button"
                  >
                    <span data-ai-id="item-detail-edit-button-label">Edit</span>
                  </a>
                </>
              ) : null}
              <Button
                type="button"
                size="sm"
                variant={favorite.isFavorited ? 'default' : 'secondary'}
                loading={favorite.isLoading}
                disabled={!item}
                onClick={() => {
                  void favorite.toggleFavoriteOrRedirect().catch(() => undefined);
                }}
                className="shrink-0"
                data-ai-id="item-detail-favorite-button"
                aiIdPrefix="item-detail-favorite-button"
                aria-pressed={favorite.isFavorited}
              >
                {favorite.isFavorited ? 'Saved' : 'Save'}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={!item}
                onClick={async () => {
                  if (!item) {
                    return;
                  }

                  try {
                    const shareUrl = new URL(buildSiteHref(`/items/${item.id}`), window.location.origin).toString();
                    await navigator.clipboard.writeText(shareUrl);
                    toast({
                      title: 'Link copied.',
                      description: 'The item URL was copied to your clipboard.',
                      tone: 'success',
                    });
                  } catch {
                    toast({
                      title: "Couldn't copy link.",
                      description: 'Please check browser permissions and try again.',
                      tone: 'error',
                    });
                  }
                }}
                className="shrink-0"
                data-ai-id="item-detail-copy-link-button"
                aiIdPrefix="item-detail-copy-link-button"
              >
                Copy link
              </Button>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="shrink-0 border-emerald-700 bg-emerald-600 text-white shadow-sm hover:bg-emerald-500 focus:border-emerald-700 focus:ring-emerald-500"
                disabled={!code}
                onClick={async () => {
                  if (!code) {
                    return;
                  }

                  try {
                    await navigator.clipboard.writeText(code);
                    toast({
                      title: 'Code copied.',
                      description: 'Copied to your clipboard.',
                      tone: 'success',
                    });
                  } catch {
                    toast({
                      title: "Couldn't copy.",
                      description: 'Please check browser permissions and try again.',
                      tone: 'error',
                    });
                  }
                }}
                data-ai-id="item-detail-quick-copy-code-button"
                aiIdPrefix="item-detail-quick-copy-code-button"
              >
                Copy code
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="shrink-0"
                onClick={onClose}
                data-ai-id="item-detail-drawer-close-button"
                aiIdPrefix="item-detail-drawer-close-button"
              >
                Close
              </Button>
            </div>
          </div>
        ) : null}

        {itemId ? <ItemDetailView itemId={itemId} mode="drawer" onClose={onClose} backToSearch={backToSearch} /> : null}
      </div>
    </DesktopDrawer>
  );
}
